/**
 * Canonical base color scales for BOTH apps (light + dark).
 *
 * Reconciled from mobile's assets/base_colors.js (canonical: richer — 18
 * families and the extra blue[60] stop) unioned with web's
 * assets/colorTheme.js STATIC_COLORS / DARK_BASE.primary. Where the two apps
 * disagreed on a stop, the decision is pinned by __tests__/theme.test.js and
 * marked with a "RECONCILED:" comment below. Everything else is byte-identical
 * to what both apps ship today.
 *
 * Scale semantics (light mode): 50 lightest … 500 brand/primary … 950 darkest.
 * The DARK scales are *visually inverted* (50 darkest … 950 lightest) so the
 * same index reads correctly across modes for text; 500 stays the brand color.
 */

export const BASE_COLORS_LIGHT = {
  blue: {
    50: "#fafcff",   // RECONCILED: mobile value (web had #eff6ff)
    60: "#e6f1f8",   // mobile-only extra stop, kept
    100: "#dbeafe",
    200: "#bfdbfe",
    300: "#93c5fd",
    400: "#60a5fa",
    500: "#0973ba", // Primary brand blue
    600: "#0861a0",
    700: "#064f86",
    800: "#053d6c",
    900: "#042b52",
    950: "#031938",
    DEFAULT: "#0973ba"
  },

  orange: {
    50: "#fffcf9",   // RECONCILED: mobile value (web had #fef7f0)
    100: "#fdeee0",
    200: "#fbd5c0",
    300: "#f8bb9f",
    400: "#f5a17f",
    500: "#f79764", // Primary brand orange
    600: "#de8550",
    700: "#c5733c",
    800: "#ac6128",
    900: "#934f14",
    950: "#7a3d00",
    DEFAULT: "#f79764"
  },

  green: {
    50: "#f9fefb",   // RECONCILED: mobile value (web had #f0fdf4)
    100: "#dcfce7",
    200: "#bbf7d0",
    300: "#86efac",
    400: "#4ade80",
    500: "#22c55e", // Primary green
    600: "#16a34a",
    700: "#15803d",
    800: "#166534",
    900: "#14532d",
    950: "#0f2419",
    DEFAULT: "#22c55e"
  },

  red: {
    50: "#fef2f2",
    100: "#fee2e2",
    200: "#fecaca",
    300: "#fca5a5",
    400: "#f87171",
    500: "#ef4444", // Primary red
    600: "#dc2626",
    700: "#b91c1c",
    800: "#991b1b",
    900: "#7f1d1d",
    950: "#450a0a",
    DEFAULT: "#ef4444"
  },

  yellow: {
    50: "#fffdf7",   // RECONCILED: mobile value (web had #fffbeb)
    100: "#fef3c7",
    200: "#fde68a",
    300: "#fcd34d",
    400: "#fbbf24",
    500: "#f59e0b", // Primary yellow
    600: "#d97706",
    700: "#b45309",
    800: "#92400e",
    900: "#78350f",
    950: "#451a03",
    DEFAULT: "#f59e0b"
  },

  gold: {
    50: "#fffbf0",
    100: "#fef5d6",
    200: "#fde8a8",
    300: "#fcd670",
    400: "#f9bd3c",
    500: "#f59e0b", // Primary gold
    600: "#d97706",
    700: "#b45309",
    800: "#92400e",
    900: "#78350f",
    950: "#451a03",
    DEFAULT: "#f59e0b"
  },

  amber: {
    50: "#fffbeb",
    100: "#fef3c7",
    200: "#fde68a",
    300: "#fcd34d",
    400: "#fbbf24",
    500: "#f59e0b", // Primary amber
    600: "#d97706",
    700: "#b45309",
    800: "#92400e",
    900: "#78350f",
    950: "#451a03",
    DEFAULT: "#f59e0b"
  },

  purple: {
    50: "#fdfbff",
    100: "#f3e8ff",
    200: "#e9d5ff",
    300: "#d8b4fe",
    400: "#c084fc",
    500: "#a855f7", // Primary purple
    600: "#9333ea",
    700: "#7c3aed",
    800: "#6b21a8",
    900: "#581c87",
    950: "#3b0764",
    DEFAULT: "#a855f7"
  },

  pink: {
    50: "#fefafc",
    100: "#fce7f3",
    200: "#fbcfe8",
    300: "#f9a8d4",
    400: "#f472b6",
    500: "#ec4899", // Primary pink
    600: "#db2777",
    700: "#be185d",
    800: "#9d174d",
    900: "#831843",
    950: "#500724",
    DEFAULT: "#ec4899"
  },

  indigo: {
    50: "#fafbff",
    100: "#e0e7ff",
    200: "#c7d2fe",
    300: "#a5b4fc",
    400: "#818cf8",
    500: "#6366f1", // Primary indigo
    600: "#4f46e5",
    700: "#4338ca",
    800: "#3730a3",
    900: "#312e81",
    950: "#1e1b4b",
    DEFAULT: "#6366f1"
  },

  teal: {
    50: "#f0fdfa",
    100: "#ccfbf1",
    200: "#99f6e4",
    300: "#5eead4",
    400: "#2dd4bf",
    500: "#14b8a6", // Primary teal
    600: "#0d9488",
    700: "#0f766e",
    800: "#115e59",
    900: "#134e4a",
    950: "#042f2e",
    DEFAULT: "#14b8a6"
  },

  cyan: {
    50: "#ecfeff",
    100: "#cffafe",
    200: "#a5f3fc",
    300: "#67e8f9",
    400: "#22d3ee",
    500: "#06b6d4", // Primary cyan
    600: "#0891b2",
    700: "#0e7490",
    800: "#155e75",
    900: "#164e63",
    950: "#083344",
    DEFAULT: "#06b6d4"
  },

  lime: {
    50: "#f7fee7",
    100: "#ecfccb",
    200: "#d9f99d",
    300: "#bef264",
    400: "#a3e635",
    500: "#84cc16", // Primary lime
    600: "#65a30d",
    700: "#4d7c0f",
    800: "#3f6212",
    900: "#365314",
    950: "#1a2e05",
    DEFAULT: "#84cc16"
  },

  emerald: {
    50: "#ecfdf5",
    100: "#d1fae5",
    200: "#a7f3d0",
    300: "#6ee7b7",
    400: "#34d399",
    500: "#10b981", // Primary emerald
    600: "#059669",
    700: "#047857",
    800: "#065f46",
    900: "#064e3b",
    950: "#022c22",
    DEFAULT: "#10b981"
  },

  violet: {
    50: "#f5f3ff",
    100: "#ede9fe",
    200: "#ddd6fe",
    300: "#c4b5fd",
    400: "#a78bfa",
    500: "#8b5cf6", // Primary violet
    600: "#7c3aed",
    700: "#6d28d9",
    800: "#5b21b6",
    900: "#4c1d95",
    950: "#2e1065",
    DEFAULT: "#8b5cf6"
  },

  fuchsia: {
    50: "#fdf4ff",
    100: "#fae8ff",
    200: "#f5d0fe",
    300: "#f0abfc",
    400: "#e879f9",
    500: "#d946ef", // Primary fuchsia
    600: "#c026d3",
    700: "#a21caf",
    800: "#86198f",
    900: "#701a75",
    950: "#4a044e",
    DEFAULT: "#d946ef"
  },

  sky: {
    50: "#f0f9ff",
    100: "#e0f2fe",
    200: "#bae6fd",
    300: "#7dd3fc",
    400: "#38bdf8",
    500: "#0ea5e9", // Primary sky
    600: "#0284c7",
    700: "#0369a1",
    800: "#075985",
    900: "#0c4a6e",
    950: "#082f49",
    DEFAULT: "#0ea5e9"
  },

  gray: {
    50: "#fafafa",
    100: "#f5f5f5",
    200: "#c3c6cb",  // RECONCILED: web value (mobile had #f5f5f5 — a duplicate of 100)
    300: "#a7aab2",  // RECONCILED: web value. Taken together with 200: reverting only
                     //   300 to mobile's #d1d5db would invert the ramp (200 darker than
                     //   300). NOTE this darkens ~25 mobile light-mode call sites
                     //   (inputs/borders/sliders/flashcards) — disclosed in PR #6;
                     //   needs a visual pass before any brand retune relies on it.
    400: "#8b909a",
    500: "#707682", // Primary gray
    600: "#565d6b",
    700: "#3e4555",
    800: "#262e3f",
    900: "#11192b",
    950: "#020617",
    DEFAULT: "#707682"
  },

  neutral: {
    white: '#ffffff',
    black: '#000000',
  }
};

export const BASE_COLORS_DARK = {
  blue: {
    50: "#020617",
    60: "#0b1a2d",   // mobile-only extra stop, kept
    100: "#0b1a2d",
    200: "#122b46",
    300: "#18415f",
    400: "#1f5680",
    500: "#0973ba", // still brand blue @500
    600: "#2d8dd3",
    700: "#4da6e0",
    800: "#7bc0ec",
    900: "#b3dcf6",
    950: "#e0f2fe",
    DEFAULT: "#2d8dd3"
  },
  orange: {
    50: "#1a0f0a",
    100: "#2d1811",
    200: "#422318",
    300: "#5c2e1f",
    400: "#7c3a28",
    500: "#f79764",
    600: "#fb923c",
    700: "#fdba74",
    800: "#fed7aa",
    900: "#feedde",
    950: "#fef7f0",
    DEFAULT: "#fb923c"
  },
  green: {
    50: "#0a1f0a",
    100: "#14291f",
    200: "#1e3a2e",
    300: "#284b3d",
    400: "#325c4c",
    500: "#22c55e",
    600: "#4ade80",
    700: "#86efac",
    800: "#bbf7d0",
    900: "#dcfce7",
    950: "#f0fdf4",
    DEFAULT: "#4ade80"
  },
  red: {
    50: "#1f0a0a",
    100: "#2d1414",
    200: "#421e1e",
    300: "#572828",
    400: "#6c3232",
    500: "#f87171",
    600: "#fca5a5",
    700: "#fecaca",
    800: "#fee2e2",
    900: "#fef2f2",
    950: "#ffffff",
    DEFAULT: "#f87171"
  },
  yellow: {
    50: "#1f1a0a",
    100: "#2d250a",
    200: "#42300a",
    300: "#573b0a",
    400: "#6c460a",
    500: "#f59e0b",
    600: "#fbbf24",
    700: "#fcd34d",
    800: "#fde68a",
    900: "#fef3c7",
    950: "#fffbeb",
    DEFAULT: "#fbbf24"
  },
  gold: {
    50: "#1a1510",
    100: "#2d2410",
    200: "#453510",
    300: "#5c4510",
    400: "#735610",
    500: "#f59e0b",
    600: "#f9bd3c",
    700: "#fcd670",
    800: "#fde8a8",
    900: "#fef5d6",
    950: "#fffbf0",
    DEFAULT: "#f9bd3c"
  },
  amber: {
    50: "#1f1a0a",
    100: "#2d250a",
    200: "#42300a",
    300: "#573b0a",
    400: "#6c460a",
    500: "#f59e0b",
    600: "#fbbf24",
    700: "#fcd34d",
    800: "#fde68a",
    900: "#fef3c7",
    950: "#fffbeb",
    DEFAULT: "#fbbf24"
  },

  purple: {
    50: "#3b0764",
    100: "#581c87",
    200: "#6b21a8",
    300: "#7c3aed",
    400: "#9333ea",
    500: "#a855f7",
    600: "#c084fc",
    700: "#d8b4fe",
    800: "#e9d5ff",
    900: "#f3e8ff",
    950: "#faf5ff",
    DEFAULT: "#c084fc"
  },

  pink: {
    50: "#500724",
    100: "#831843",
    200: "#9d174d",
    300: "#be185d",
    400: "#db2777",
    500: "#ec4899",
    600: "#f472b6",
    700: "#f9a8d4",
    800: "#fbcfe8",
    900: "#fce7f3",
    950: "#fdf2f8",
    DEFAULT: "#f472b6"
  },

  indigo: {
    50: "#1e1b4b",
    100: "#312e81",
    200: "#3730a3",
    300: "#4338ca",
    400: "#4f46e5",
    500: "#6366f1",
    600: "#818cf8",
    700: "#a5b4fc",
    800: "#c7d2fe",
    900: "#e0e7ff",
    950: "#eef2ff",
    DEFAULT: "#818cf8"
  },

  teal: {
    50: "#042f2e",
    100: "#134e4a",
    200: "#115e59",
    300: "#0f766e",
    400: "#0d9488",
    500: "#14b8a6",
    600: "#2dd4bf",
    700: "#5eead4",
    800: "#99f6e4",
    900: "#ccfbf1",
    950: "#f0fdfa",
    DEFAULT: "#2dd4bf"
  },

  cyan: {
    50: "#083344",
    100: "#164e63",
    200: "#155e75",
    300: "#0e7490",
    400: "#0891b2",
    500: "#06b6d4",
    600: "#22d3ee",
    700: "#67e8f9",
    800: "#a5f3fc",
    900: "#cffafe",
    950: "#ecfeff",
    DEFAULT: "#22d3ee"
  },

  lime: {
    50: "#1a2e05",
    100: "#365314",
    200: "#3f6212",
    300: "#4d7c0f",
    400: "#65a30d",
    500: "#84cc16",
    600: "#a3e635",
    700: "#bef264",
    800: "#d9f99d",
    900: "#ecfccb",
    950: "#f7fee7",
    DEFAULT: "#a3e635"
  },

  emerald: {
    50: "#022c22",
    100: "#064e3b",
    200: "#065f46",
    300: "#047857",
    400: "#059669",
    500: "#10b981",
    600: "#34d399",
    700: "#6ee7b7",
    800: "#a7f3d0",
    900: "#d1fae5",
    950: "#ecfdf5",
    DEFAULT: "#34d399"
  },

  violet: {
    50: "#2e1065",
    100: "#4c1d95",
    200: "#5b21b6",
    300: "#6d28d9",
    400: "#7c3aed",
    500: "#8b5cf6",
    600: "#a78bfa",
    700: "#c4b5fd",
    800: "#ddd6fe",
    900: "#ede9fe",
    950: "#f5f3ff",
    DEFAULT: "#a78bfa"
  },

  fuchsia: {
    50: "#4a044e",
    100: "#701a75",
    200: "#86198f",
    300: "#a21caf",
    400: "#c026d3",
    500: "#d946ef",
    600: "#e879f9",
    700: "#f0abfc",
    800: "#f5d0fe",
    900: "#fae8ff",
    950: "#fdf4ff",
    DEFAULT: "#e879f9"
  },

  sky: {
    50: "#082f49",
    100: "#0c4a6e",
    200: "#075985",
    300: "#0369a1",
    400: "#0284c7",
    500: "#0ea5e9",
    600: "#38bdf8",
    700: "#7dd3fc",
    800: "#bae6fd",
    900: "#e0f2fe",
    950: "#f0f9ff",
    DEFAULT: "#38bdf8"
  },

  gray: {
    50: "#0f1419",
    100: "#1E1F25",  // RECONCILED: mobile value, the historic dark surface color (web had #1a1f28)
    200: "#252b37",
    300: "#303746",
    400: "#3b4255",
    500: "#707682",
    600: "#8b909a",
    700: "#a7aab2",
    800: "#c3c6cb",
    900: "#e2e4e7",
    950: "#f8f9fa",
    DEFAULT: "#8b909a"
  },

  neutral: {
    white: "#000000",
    black: "#ffffff"
  }
};

/**
 * @deprecated Back-compat alias for web, whose colorTheme.js exported the
 * light scales as STATIC_COLORS. Prefer BASE_COLORS_LIGHT.
 */
export const STATIC_COLORS = BASE_COLORS_LIGHT;
